const HOST_KEY = "shopify_embedded_host";
const SHOP_KEY = "shopify_embedded_shop";
const EMBEDDED_KEY = "shopify_embedded_flag";

export interface ShopifyEmbeddedContext {
  embedded: boolean;
  host: string | null;
  shop: string | null;
}

export function getShopifyEmbeddedContext(searchParams?: URLSearchParams): ShopifyEmbeddedContext {
  const params = searchParams ?? getCurrentSearchParams();

  const paramHost = params?.get("host")?.trim() || null;
  const paramShop = params?.get("shop")?.trim() || null;
  const paramEmbedded = params?.get("embedded");

  const storedHost = readSessionValue(HOST_KEY);
  const storedShop = readSessionValue(SHOP_KEY);
  const storedEmbedded = readSessionValue(EMBEDDED_KEY) === "1";

  const host = paramHost || storedHost;
  const shop = paramShop || storedShop;
  const hasExplicitEmbeddedSignal =
    paramEmbedded === "1" ||
    Boolean(paramHost) ||
    Boolean(paramShop) ||
    params?.has("id_token") === true;
  const embedded = hasExplicitEmbeddedSignal
    ? Boolean(host && shop)
    : storedEmbedded && Boolean(storedHost && storedShop);

  return { embedded, host, shop };
}

export function setShopifyEmbeddedContext(context: {
  embedded?: boolean | null;
  host?: string | null;
  shop?: string | null;
}): void {
  if (typeof window === "undefined") {
    return;
  }

  persistSessionValue(HOST_KEY, context.host);
  persistSessionValue(SHOP_KEY, context.shop);

  if (context.embedded === undefined) {
    return;
  }

  if (context.embedded) {
    sessionStorage.setItem(EMBEDDED_KEY, "1");
    return;
  }

  sessionStorage.removeItem(EMBEDDED_KEY);
}

export function clearShopifyEmbeddedContext(): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(HOST_KEY);
  sessionStorage.removeItem(SHOP_KEY);
  sessionStorage.removeItem(EMBEDDED_KEY);
}

export function buildEmbeddedAppPath(
  path: string,
  extraParams?: URLSearchParams | Record<string, string | null | undefined>,
): string {
  const url = new URL(path, "https://shakatah.local");
  const params = new URLSearchParams(url.search);
  const context = getShopifyEmbeddedContext();

  if (extraParams instanceof URLSearchParams) {
    extraParams.forEach((value, key) => {
      if (!value) {
        params.delete(key);
        return;
      }
      params.set(key, value);
    });
  } else if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (!value) {
        params.delete(key);
        return;
      }
      params.set(key, value);
    });
  }

  if (context.host) {
    params.set("host", context.host);
  }

  if (context.shop) {
    params.set("shop", context.shop);
  }

  if (context.embedded && context.host) {
    params.set("embedded", "1");
  }

  const search = params.toString();
  return `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
}

export function isShopifyEmbeddedRoute(pathname: string): boolean {
  return (
    pathname === "/merchant-login" ||
    pathname === "/login" ||
    pathname === "/oauth-success" ||
    pathname === "/shopify-redirect" ||
    pathname.startsWith("/merchant") ||
    pathname.startsWith("/admin")
  );
}

export function isEmbeddedShopifyLaunch(searchParams?: URLSearchParams): boolean {
  const params = searchParams ?? getCurrentSearchParams();
  const context = getShopifyEmbeddedContext(params ?? undefined);

  return (
    context.embedded ||
    params?.get("embedded") === "1" ||
    params?.has("id_token") === true ||
    Boolean(params?.get("host"))
  );
}

export function openShopifyTopLevel(url: string): void {
  if (typeof window === "undefined") {
    return;
  }

  window.open(url, "_top");
}

export function isRunningInsideShopifyAdmin(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.location.hostname === "admin.shopify.com") {
    return true;
  }

  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

function getCurrentSearchParams(): URLSearchParams | null {
  if (typeof window === "undefined") {
    return null;
  }

  return new URLSearchParams(window.location.search);
}

function readSessionValue(key: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return sessionStorage.getItem(key);
}

function persistSessionValue(key: string, value?: string | null) {
  if (typeof window === "undefined") {
    return;
  }

  if (!value) {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, value);
}
